/**
 * @file Suivi des ressources GPU par portee (global / biome / mode) et liberation EXPLICITE (dispose) : aucune fuite de geometrie/materiau/texture.
 */
import type { BufferGeometry, Material, Texture } from 'three';

export type Disposable = BufferGeometry | Material | Texture | { dispose(): void };

/**
 * Portees :
 *  - 'global' : vit toute la session (gradient toon, materiaux d'outline, geometries de base des chats/armes/projectiles).
 *  - 'biome'  : sol/decors du biome courant -> liberee au changement de biome.
 *  - 'mode'   : objets propres a un mode (barriere Monster, arene du hub) -> liberee a la sortie du mode.
 */
export type ResourceScope = 'global' | 'biome' | 'mode';

export class ResourceTracker {
  private readonly scopes: Record<ResourceScope, Set<Disposable>> = { global: new Set(), biome: new Set(), mode: new Set() };

  track<T extends Disposable>(res: T, scope: ResourceScope = 'global'): T {
    this.scopes[scope].add(res);
    return res;
  }

  release(scope: ResourceScope): number {
    const set = this.scopes[scope];
    const n = set.size;
    for (const r of set) r.dispose();
    set.clear();
    return n;
  }

  releaseAll(): void {
    this.release('mode');
    this.release('biome');
    this.release('global');
  }

  count(scope?: ResourceScope): number {
    return scope ? this.scopes[scope].size : this.scopes.global.size + this.scopes.biome.size + this.scopes.mode.size;
  }
}
